import React, { useState } from 'react';
import { Zap, FileSpreadsheet, Boxes, KeyRound } from 'lucide-react';
import { DickerDataTab } from '../DickerDataTab/DickerDataTab';
import { TelstraDistributorTab } from '../TelstraDistributorTab/TelstraDistributorTab';
import { IngramTab } from '../IngramTab/IngramTab';
import { Pax8Tab } from '../Pax8Tab/Pax8Tab';
import { cn } from '../../lib/utils';

/**
 * Combined Distributors Tab
 *
 * Secondary sidebar navigation listing each distributor,
 * with the selected distributor's content loaded alongside.
 */

type DistributorKey = 'dicker-data' | 'ingram' | 'telstra' | 'pax8';

interface DistributorNavItem {
  id: DistributorKey;
  name: string;
  description: string;
  icon: React.ElementType;
}

const distributors: DistributorNavItem[] = [
  {
    id: 'dicker-data',
    name: 'Dicker Data',
    description: 'B2B API pricing & stock',
    icon: Zap,
  },
  {
    id: 'ingram',
    name: 'Ingram Micro',
    description: 'Xvantage catalog & P&A',
    icon: Boxes,
  },
  {
    id: 'telstra',
    name: 'Telstra',
    description: 'Mobile hardware uploads',
    icon: FileSpreadsheet,
  },
  {
    id: 'pax8',
    name: 'Pax8',
    description: 'Microsoft licence standard pricing',
    icon: KeyRound,
  },
];

interface DistributorsTabProps {
  onCatalogueChanged?: () => void;
}

export function DistributorsTab({ onCatalogueChanged }: DistributorsTabProps) {
  const [selected, setSelected] = useState<DistributorKey>('dicker-data');

  return (
    <div className="h-full w-full flex-1 min-w-0 flex">
      {/* Secondary Sidebar - Duralux style */}
      <div className="w-[260px] flex-shrink-0 bg-white border-r border-slate-200">
        <div className="sticky top-0">
          {/* Header */}
          <div className="h-14 px-6 flex items-center border-b border-slate-200">
            <h4 className="text-base font-bold text-slate-900">Distributors</h4>
          </div>

          {/* Navigation */}
          <div className="py-3 px-3">
            <nav className="space-y-1">
              {distributors.map((dist) => {
                const Icon = dist.icon;
                const isActive = selected === dist.id;
                return (
                  <button
                    key={dist.id}
                    onClick={() => setSelected(dist.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all text-[13px] font-semibold',
                      isActive
                        ? 'bg-orange-50 text-orange-700'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-4 w-4 shrink-0',
                        isActive ? 'text-orange-600' : 'text-slate-400'
                      )}
                    />
                    <div className="text-left min-w-0">
                      <span className="block truncate">{dist.name}</span>
                      <span className={cn(
                        'block text-[11px] font-normal truncate',
                        isActive ? 'text-orange-500' : 'text-slate-400'
                      )}>
                        {dist.description}
                      </span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content — `min-w-0` is essential so the flex child can shrink below its
          intrinsic content width. `overflow-hidden` lets each distributor page own
          its vertical/horizontal scroll regions so wide tables cannot stretch the
          shared connector header. */}
      <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
        {selected === 'dicker-data' && <DickerDataTab />}
        {selected === 'ingram' && <IngramTab />}
        {selected === 'telstra' && <TelstraDistributorTab onCatalogueChanged={onCatalogueChanged} />}
        {selected === 'pax8' && <Pax8Tab />}
      </div>
    </div>
  );
}

export default DistributorsTab;
